import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';

export default function Docs() {
  const [activeSection, setActiveSection] = useState('auth');
  const [copied, setCopied] = useState({});

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
  };

  const sections = [
    { id: 'auth', title: 'Authentication' },
    { id: 'payin', title: 'Create Payment' },
    { id: 'webhook', title: 'Webhooks' },
    { id: 'payout', title: 'Payout API' },
    { id: 'status', title: 'Status Codes' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY + 200; // offset for fixed header
      let current = 'auth';

      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrolled) {
          current = section.id;
        }
      }

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // sections is static → safe to omit

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const codeSamples = {
    auth: `curl https://api.zindi.pk/v1/payments \\
  -H "X-API-Key: sk_live_your_key_here" \\
  -H "Content-Type: application/json"`,
    payinNode: `const response = await fetch('https://api.zindi.pk/v1/payments', {
  method: 'POST',
  headers: {
    'X-API-Key': 'sk_live_abc123',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    amount: 5000,
    currency: 'PKR',
    method: 'jazzcash',
    redirect_url: 'https://yoursite.com/success'
  })
});`,
    webhookVerify: `const crypto = require('crypto');
const signature = req.headers['x-zindi-signature'];
const payload = req.rawBody;
const expected = crypto
  .createHmac('sha256', 'your_webhook_secret')
  .update(payload)
  .digest('hex');
if (signature !== expected) throw new Error('Invalid');`
  };

  return (
    <div className="pt-28 pb-20 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
      <div className="container mx-auto px-6">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 font-poppins bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            API Documentation
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto">
            Integrate in 10 minutes. Accept payments like a pro.
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={() => scrollToSection('payin')}
              className="bg-primary text-white px-8 py-4 rounded-xl font-bold hover:shadow-xl transition"
            >
              Quick Start
            </button>
            {/* <a
              href="https://dashboard.zindi.pk"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-primary text-primary px-8 py-4 rounded-xl font-bold hover:bg-primary hover:text-white transition"
            >
              Get API Keys
            </a> */}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="sticky top-32 bg-white rounded-3xl shadow-xl p-6 space-y-2">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left py-4 px-6 rounded-2xl font-semibold transition-all ${
                    activeSection === s.id
                      ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg'
                      : 'hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="lg:col-span-3 space-y-24">
            {/* Authentication */}
            <section id="auth">
              <h2 className="text-4xl font-bold mb-8 font-poppins">Authentication</h2>
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Every request needs your <span className="font-bold text-primary">Secret Key</span>. 
                  Find it in <a href="https://zindipay/portal.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">Dashboard → API Keys</a>.
                </p>
                <div className="bg-gray-900 text-gray-100 p-6 rounded-2xl font-mono text-sm relative overflow-x-auto">
                  <pre>{codeSamples.auth}</pre>
                  <button
                    onClick={() => copyToClipboard(codeSamples.auth, 'auth')}
                    className="absolute top-4 right-4 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition"
                    aria-label="Copy code"
                  >
                    {copied['auth'] ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-6 flex items-center gap-3 text-sm">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <span>Never expose your Secret Key in frontend code!</span>
                </div>
              </div>
            </section>

            {/* Create Payment */}
            <section id="payin">
              <h2 className="text-4xl font-bold mb-8 font-poppins">Create Payment</h2>
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <p className="text-lg text-gray-600 mb-6">
                  Start a payment in <span className="text-primary font-bold">one API call</span>. 
                  Supports Easypaisa, JazzCash, Bank, Card.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-xl mb-4">Node.js Example</h3>
                    <pre className="bg-gray-900 text-gray-100 p-6 rounded-2xl text-xs overflow-x-auto relative">
                      <code>{codeSamples.payinNode}</code>
                      <button
                        onClick={() => copyToClipboard(codeSamples.payinNode, 'payinNode')}
                        className="absolute top-4 right-4 p-2 bg-gray-800 rounded-lg"
                        aria-label="Copy code"
                      >
                        {copied['payinNode'] ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </pre>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-4">Response</h3>
                    <pre className="bg-green-50 text-green-800 p-6 rounded-2xl text-xs overflow-x-auto">
{`{
  "id": "pay_1A2B3C",
  "status": "pending",
  "payment_url": "https://pay.zindi.pk/pay_1A2B3C",
  "expires_at": "2025-11-05T15:30:00Z"
}`}
                    </pre>
                  </div>
                </div>
                <div className="mt-8 p-6 bg-blue-50 rounded-2xl">
                  <p className="font-semibold text-primary">Pro Tip:</p>
                  <p>Use <code className="bg-white px-2 py-1 rounded">idempotency_key</code> to prevent duplicate charges!</p>
                </div>
              </div>
            </section>

            {/* Webhooks */}
            <section id="webhook">
              <h2 className="text-4xl font-bold mb-8 font-poppins">Webhooks</h2>
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <p className="text-lg text-gray-600 mb-6">
                  Get <span className="text-primary font-bold">instant notifications</span> when payment succeeds, fails, or refunds.
                </p>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-xl mb-3">Events You’ll Receive</h3>
                    <ul className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['payment.succeeded', 'payment.failed', 'payout.sent', 'refund.created'].map(e => (
                        <li key={e} className="bg-gradient-to-r from-primary to-accent text-white px-4 py-3 rounded-xl text-center font-medium text-xs">
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-3">Verify Signature (Node.js)</h3>
                    <pre className="bg-gray-900 text-gray-100 p-6 rounded-2xl text-xs relative overflow-x-auto">
                      <code>{codeSamples.webhookVerify}</code>
                      <button
                        onClick={() => copyToClipboard(codeSamples.webhookVerify, 'webhook')}
                        className="absolute top-4 right-4 p-2 bg-gray-800 rounded-lg"
                        aria-label="Copy code"
                      >
                        {copied['webhook'] ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </pre>
                  </div>
                </div>
              </div>
            </section>

            {/* Payout API */}
            <section id="payout">
              <h2 className="text-4xl font-bold mb-8 font-poppins">Payout API</h2>
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <p className="text-lg text-gray-600 mb-6">
                  Send USDT to any TRC20 wallet <span className="text-primary font-bold">in 2 seconds</span>.
                </p>
                <div className="bg-gradient-to-br from-primary to-accent text-white p-8 rounded-3xl">
                  <h3 className="text-2xl font-bold mb-4">Same-Day Settlement</h3>
                  <p className="text-lg opacity-90">
                    Cutoff: 3 PM PKT → Funds in wallet by 6 PM<br />
                    After 3 PM → Next day 10 AM
                  </p>
                </div>
                <pre className="mt-6 bg-gray-900 text-gray-100 p-6 rounded-2xl text-sm overflow-x-auto">
{`POST /v1/payouts
{
  "amount": 4900,
  "wallet": "TABC123...",
  "reference": "ORD-789"
}`}
                </pre>
              </div>
            </section>

            {/* Status Codes */}
            <section id="status">
              <h2 className="text-4xl font-bold mb-8 font-poppins">Status Codes</h2>
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="py-4">Code</th>
                      <th className="py-4">Meaning</th>
                      <th className="py-4">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b">
                      <td className="py-4"><code className="bg-green-100 text-green-800 px-3 py-1 rounded">success</code></td>
                      <td className="py-4">Payment complete</td>
                      <td className="py-4">Fulfill order</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-4"><code className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded">pending</code></td>
                      <td className="py-4">User on payment screen</td>
                      <td className="py-4">Show loader</td>
                    </tr>
                    <tr>
                      <td className="py-4"><code className="bg-red-100 text-red-800 px-3 py-1 rounded">failed</code></td>
                      <td className="py-4">User cancelled or error</td>
                      <td className="py-4">Let user retry</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-24">
          <div className="bg-gradient-to-r from-primary to-accent text-white rounded-3xl p-12 shadow-2xl inline-block">
            <h2 className="text-4xl font-bold mb-6">Ready to Go Live?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join 500+ merchants already earning in USDT
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-primary px-12 py-5 rounded-xl text-xl font-bold hover:scale-110 transition shadow-xl"
            >
              Start Free → No Card Needed
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}